import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Copy, Loader2, PlugZap, RefreshCw } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STAGE_LABEL = {
  register: "Registro do cliente", authorize: "Tela de autorização", consent: "Consentimento",
  token: "Troca de token", token_refresh: "Renovação de token", mcp_request: "Chamada MCP", http: "Requisição HTTP",
};

const fields = (event) => Object.entries(event).filter(([k]) => !["at", "stage", "outcome"].includes(k));

export default function McpDiagnostics() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(true);
  const [selftest, setSelftest] = useState(null);
  const [situacao, setSituacao] = useState(null);
  const [testing, setTesting] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings/mcp-diagnostics?limit=120");
      setEvents(r.data?.events || []);
      setSituacao(r.data?.situacao || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar o log de conexão");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    if (auto) timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, [auto]); // eslint-disable-line

  const runSelftest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/mcp-selftest");
      setSelftest(r.data);
      r.data?.ok ? toast.success("Conexão MCP validada ponta a ponta") : toast.error("Uma etapa do autoteste falhou");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao executar o autoteste");
    } finally { setTesting(false); }
  };

  const plain = useMemo(() => events.map((e) => `${e.at} ${e.stage} ${e.outcome} ` +
    fields(e).map(([k, v]) => `${k}=${v}`).join(" ")).join("\n"), [events]);

  const copy = async (value, label) => {
    try { await navigator.clipboard.writeText(value); toast.success(`${label} copiado`); }
    catch { toast.error("Não foi possível copiar"); }
  };

  return (
    <div data-testid="mcp-diagnostics-page" className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Log de conexão MCP</h1>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Cada etapa das tentativas de conexão do Claude aparece aqui em tempo real: registro do cliente,
            tela de autorização, consentimento, troca de token e as chamadas autenticadas. Nenhum token,
            senha ou hash é gravado.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
            <Label htmlFor="auto-refresh" className="text-xs text-muted-foreground">Atualizar automático</Label>
            <Switch id="auto-refresh" data-testid="mcp-log-auto" checked={auto} onCheckedChange={setAuto} />
          </div>
          <Button data-testid="mcp-log-refresh" variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />} Atualizar
          </Button>
          <Button data-testid="mcp-log-copy" variant="outline" onClick={() => copy(plain, "Log")}>
            <Copy className="size-4" /> Copiar log
          </Button>
          <Button data-testid="mcp-log-selftest" onClick={runSelftest} disabled={testing}>
            {testing ? <Loader2 className="size-4 animate-spin" /> : <PlugZap className="size-4" />} Testar conexão agora
          </Button>
        </div>
      </div>

      {situacao && (
        <Card data-testid="mcp-log-situacao" className={situacao.etapa === "conectado" ? "border-emerald-500/40" : "border-amber-500/40"}>
          <CardContent className="space-y-1 p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{situacao.titulo}</span>
              <Badge variant="outline">{situacao.etapa}</Badge>
              {situacao.codigos_sem_troca > 0 && (
                <Badge data-testid="mcp-log-pending-codes" variant="outline" className="border-amber-500/50 text-amber-600">
                  {situacao.codigos_sem_troca} código(s) autorizado(s) sem troca de token
                </Badge>
              )}
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">{situacao.diagnostico}</p>
            <p className="text-sm leading-relaxed">{situacao.sugestao}</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/75">
        <CardContent className="grid gap-3 p-5 md:grid-cols-2">
          {[["Endereço MCP real (dados da consultoria)", `${API_BASE}/api/mcp`],
            ["Endereço MCP de teste (dados fictícios)", `${API_BASE}/api/teste/mcp`]].map(([label, url]) => (
            <div key={url} className="rounded-xl border border-border/75 p-4">
              <div className="label-tech">{label}</div>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-muted/40 px-2 py-1 text-xs">{url}</code>
                <Button data-testid={`copy-${url.includes("teste") ? "test" : "real"}-url`} size="sm" variant="ghost" onClick={() => copy(url, "Endereço")}>
                  <Copy className="size-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {selftest && (
        <Card data-testid="mcp-log-selftest-result" className={selftest.ok ? "border-emerald-500/40" : "border-amber-500/40"}>
          <CardContent className="space-y-2 p-5">
            <div className="text-sm font-medium">{selftest.ok ? "Conexão funcionando" : "Conexão com falha"} · <span className="font-mono text-xs text-muted-foreground">{selftest.tested_url}</span></div>
            {(selftest.steps || []).map((step, i) => (
              <div key={step.step} data-testid={`selftest-step-${i}`} className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant={step.ok ? "secondary" : "outline"}>{step.ok ? "ok" : "falha"}</Badge>
                <span className="font-medium">{step.step}</span>
                {step.status != null && <span className="font-mono text-muted-foreground">HTTP {step.status}</span>}
                <span className="break-all text-muted-foreground">{step.detail}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-border/75">
        <CardContent className="p-5">
          {events.length === 0 ? (
            <p data-testid="mcp-log-empty" className="text-sm text-muted-foreground">Nenhuma tentativa registrada ainda. Peça ao Claude para conectar e o log aparece aqui.</p>
          ) : (
            <ul data-testid="mcp-log-list" className="space-y-2">
              {events.map((event, index) => (
                <li key={`${event.at}-${index}`} data-testid={`mcp-log-event-${index}`}
                    className={`rounded-lg border-l-2 bg-muted/20 p-3 text-xs ${event.outcome === "ok" || event.outcome === "200" || event.outcome === "201" ? "border-l-emerald-500/60" : "border-l-amber-500/60"}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{STAGE_LABEL[event.stage] || event.stage}</span>
                    <Badge variant={event.outcome === "ok" ? "secondary" : "outline"}>{event.outcome}</Badge>
                    <span className="font-mono text-muted-foreground">{new Date(event.at).toLocaleString("pt-BR")}</span>
                  </div>
                  <div className="mt-1 break-all font-mono text-[11px] leading-relaxed text-muted-foreground">
                    {fields(event).map(([k, v]) => `${k}=${v}`).join("  ")}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
